import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { db, findByBarcode } from '../lib/db'
import { money } from '../lib/money'
import { useBarcodeScanner } from '../lib/scanner'
import { isOwner, useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { CategoryManager } from '../components/CategoryManager'
import { blankDraft, ProductEditor, toDraft, type ProductDraft } from '../components/ProductEditor'

export function ProductsScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const owner = isOwner(useApp((s) => s.session?.role))

  const [query, setQuery] = useState('')
  const [catId, setCatId] = useState<string | null>(null)
  const [editing, setEditing] = useState<ProductDraft | null>(null)
  const [groups, setGroups] = useState(false)

  const categories = useLiveQuery(() => db.categories.orderBy('sortOrder').toArray(), [], [])
  const products = useLiveQuery(() => db.products.orderBy('sortOrder').toArray(), [], [])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return products.filter((p) => {
      if (catId === 'none' ? p.categoryId !== null : catId && p.categoryId !== catId) return false
      if (!q) return true
      return p.name.toLowerCase().includes(q) || (p.barcode ?? '').includes(q)
    })
  }, [products, catId, query])

  const nameOf = (id: string | null) => categories.find((c) => c.id === id)?.name ?? 'Chưa xếp nhóm'

  /*
   * Quét mã ở màn hình này là đường nhanh nhất để tiệm tạp hoá dựng danh mục:
   * cầm món hàng lên quét — có rồi thì mở ra sửa giá, chưa có thì mở luôn form
   * thêm mới với mã đã điền sẵn. Không phải gõ lại dãy 13 số bao giờ.
   */
  useBarcodeScanner({
    enabled: !editing && !groups,
    onScan: async (code) => {
      const found = await findByBarcode(code)
      if (found) {
        setEditing(toDraft(found))
        say(`Đã có: ${found.name}`)
      } else {
        setEditing(blankDraft(catId === 'none' ? null : (catId ?? categories[0]?.id ?? null), code))
        say(`Mã ${code} chưa có — thêm món mới`)
      }
    },
  })

  // Máy chủ đã chặn mọi lệnh sửa danh mục. Chặn thêm ở đây để nhân viên không mở ra
  // rồi bấm gì cũng báo lỗi — thà nói thẳng ngay từ đầu.
  if (!owner) {
    return (
      <div className="screen">
        <AppBar title="Sản phẩm" onBack={() => nav('/')} />
        <div className="body center">
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🔒
            </div>
            <p>Chỉ chủ quán sửa được sản phẩm và giá.</p>
          </div>
        </div>
      </div>
    )
  }

  async function afterSave() {
    setEditing(null)
    await refreshCatalog()
  }

  return (
    <div className="screen">
      <AppBar
        title="Sản phẩm"
        sub={`${products.length} món · ${products.filter((p) => p.barcode).length} món có mã vạch`}
        onBack={() => nav('/settings')}
        right={
          <button
            type="button"
            className="icon-btn"
            aria-label="Sửa nhóm món"
            title="Sửa nhóm món"
            onClick={() => setGroups(true)}
          >
            🗂
          </button>
        }
      />

      <div className="body">
        <label className="field">
          <span className="sr-only">Tìm món</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm theo tên món hoặc mã vạch"
            inputMode="search"
          />
        </label>

        <p className="hint">
          Cầm món hàng lên quét là xong: có rồi thì mở ra sửa giá, chưa có thì thêm mới với mã đã
          điền sẵn.
        </p>

        <div className="tabs tabs-inline" role="tablist" aria-label="Nhóm món">
          <button type="button" role="tab" aria-selected={catId === null} onClick={() => setCatId(null)}>
            Tất cả
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={c.id === catId}
              onClick={() => setCatId(c.id)}
            >
              {c.name}
            </button>
          ))}
          {products.some((p) => !p.categoryId) && (
            <button
              type="button"
              role="tab"
              aria-selected={catId === 'none'}
              onClick={() => setCatId('none')}
            >
              Chưa xếp nhóm
            </button>
          )}
        </div>

        {shown.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              📋
            </div>
            <p>{query ? `Không có món nào khớp "${query}".` : 'Nhóm này chưa có món nào.'}</p>
          </div>
        ) : (
          <div className="list">
            {shown.map((p) => (
              <button
                key={p.id}
                type="button"
                className="row-btn"
                onClick={() => setEditing(toDraft(p))}
              >
                <span className="row-text">
                  <b>{p.name}</b>
                  <small>
                    {nameOf(p.categoryId)}
                    {p.soldByWeight ? ` · theo ${p.unit}` : ''}
                    {p.barcode ? ` · ${p.barcode}` : ''}
                  </small>
                </span>
                {p.barcode && (
                  <span className="tag-scan" aria-label="Có mã vạch" title="Có mã vạch">
                    ▮▮▮
                  </span>
                )}
                <span className="row-price num">{money(p.price)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="dock">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => nav('/settings/products/scan')}
        >
          📷 Chụp bảng giá
        </button>
        <button
          type="button"
          className="btn btn-brand grow"
          onClick={() => setEditing(blankDraft(catId === 'none' ? null : (catId ?? categories[0]?.id ?? null)))}
        >
          + Thêm món
        </button>
      </div>

      {editing && (
        <ProductEditor
          draft={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={afterSave}
        />
      )}

      {groups && (
        <CategoryManager
          categories={categories}
          products={products}
          onClose={() => setGroups(false)}
          onChanged={refreshCatalog}
        />
      )}
    </div>
  )
}
